import React, { useState, useMemo, useEffect, useRef } from 'react';
import { collection, onSnapshot, query, doc, updateDoc, serverTimestamp, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';

// ── Travel-lead intent scoring ──────────────────────────────────────────────────
// The scraper already tags each lead seeker vs. provider (category/isProvider). Here
// we derive a display "intent" from the post text so the feed can rank the strongest
// leads first and so the tiers can be retuned without re-scraping. Three tiers:
//   hot      → a seeker with urgency or a specific trip (the leads worth DMing first)
//   warm     → a plain "wants a travel agent" seeker
//   provider → an agent advertising (kept but demoted, never silently dropped)

const URGENT_RX = /\b(asap|urgent|urgently|right away|immediately|today|tonight|tomorrow|this week|this weekend|need (a |an |one |someone )?(now|today)|last[\s-]?minute|leaving soon|by (monday|tuesday|wednesday|thursday|friday|saturday|sunday|next week))\b/i;
const TRIP_RX = /\b(cruise|disney|universal|honeymoon|babymoon|anniversary|destination wedding|europe|italy|greece|paris|cabo|cancun|mexico|hawaii|caribbean|bahamas|jamaica|punta cana|dominican|sandals|all[\s-]?inclusive|resort|vegas|alaska|safari|maldives|costa rica)\b/i;

function travelIntent(entry) {
  const t = (entry.postText || '').trim();
  if (entry.isProvider || entry.category === 'provider')
    return { tier: 'provider', score: 0, label: '📣 Agent', color: '#9ca3af', bg: '#f9fafb', border: '#e5e7eb',
      reason: 'Looks like an agent advertising — not a lead.' };
  if (URGENT_RX.test(t))
    return { tier: 'hot', score: 3, label: '🔥 Hot', color: '#dc2626', bg: '#fff5f5', border: '#fecaca',
      reason: 'Time-sensitive — mentions urgency (asap / now / this week).' };
  if (TRIP_RX.test(t))
    return { tier: 'hot', score: 3, label: '🔥 Hot', color: '#dc2626', bg: '#fff5f5', border: '#fecaca',
      reason: 'Mentions a specific trip (cruise, Disney, honeymoon, etc.).' };
  return { tier: 'warm', score: 2, label: '✈️ Wants agent', color: '#b45309', bg: '#fffbeb', border: '#fde68a',
    reason: null };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseDate(str) {
  if (!str) return null;
  try { const d = new Date(String(str).replace(' at ', ' ')); return isNaN(d) ? null : d; } catch { return null; }
}
function toYMD(date) { return date.toLocaleDateString('en-CA'); }
function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }

const PRESETS = [
  { label: 'All time',  key: 'all' },
  { label: 'Today',     key: 'today' },
  { label: 'Yesterday', key: 'yesterday' },
  { label: 'Last 7d',   key: '7d' },
  { label: 'Last 30d',  key: '30d' },
  { label: 'Custom',    key: 'custom' },
];

function getPresetRange(key) {
  const now = new Date();
  const sod = startOfDay(now);
  if (key === 'today') return { from: sod, to: now };
  if (key === 'yesterday') {
    const s = new Date(sod); s.setDate(s.getDate() - 1);
    const e = new Date(sod); e.setMilliseconds(-1);
    return { from: s, to: e };
  }
  if (key === '7d')  { const s = new Date(sod); s.setDate(s.getDate() - 6);  return { from: s, to: now }; }
  if (key === '30d') { const s = new Date(sod); s.setDate(s.getDate() - 29); return { from: s, to: now }; }
  return null;
}

// Stable per-person key so we never DM the same lead twice (prefer FB profile URL).
function personKey(c) {
  const url = (c.profileUrl || '').trim();
  if (url) {
    try {
      const u = new URL(url, 'https://www.facebook.com');
      const id = u.searchParams.get('id');
      if (id) return `id:${id}`;
      const path = u.pathname.replace(/\/+$/, '').toLowerCase();
      if (path && path !== '/') return `url:${path}`;
    } catch (_) { return `url:${url.toLowerCase()}`; }
  }
  const name = (c.name || '').trim().toLowerCase();
  return name ? `name:${name}` : `id:${c.id}`;
}

// ── Avatar ────────────────────────────────────────────────────────────────────

function Avatar({ src, name, size = 40 }) {
  const [err, setErr] = useState(false);
  const initials = name ? name.split(' ').map(w => w[0]).slice(0, 2).join('') : '?';
  const palette = ['#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ef4444', '#14b8a6'];
  const bg = palette[name ? name.charCodeAt(0) % palette.length : 0];
  if (!src || err) return (
    <div style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, background: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.35, fontWeight: 700, color: '#fff', letterSpacing: '-0.5px' }}>
      {initials}
    </div>
  );
  return <img src={src} alt={name} referrerPolicy="no-referrer" onError={() => setErr(true)}
    style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />;
}

// ── Mini Calendar ─────────────────────────────────────────────────────────────

function Calendar({ from, to, onChange }) {
  const [viewYear, setViewYear] = useState(() => new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => new Date().getMonth());
  const [picking, setPicking] = useState('from');
  const [hovered, setHovered] = useState(null);

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDow    = new Date(viewYear, viewMonth, 1).getDay();

  function prev() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }
  function next() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }
  function pick(day) {
    const d = startOfDay(new Date(viewYear, viewMonth, day));
    if (picking === 'from') { onChange({ from: d, to: null }); setPicking('to'); }
    else {
      const end = new Date(d); end.setHours(23, 59, 59, 999);
      onChange(d < from ? { from: d, to: new Date(from).setHours(23, 59, 59, 999) && from } : { from, to: end });
      setPicking('from');
    }
  }
  function inRange(day) {
    const d = new Date(viewYear, viewMonth, day);
    const hi = to || hovered;
    if (!from || !hi) return false;
    return d >= (from < hi ? from : hi) && d <= (from < hi ? hi : from);
  }

  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const DOW    = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <button onClick={prev} style={calNavBtn}>‹</button>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>{MONTHS[viewMonth]} {viewYear}</span>
        <button onClick={next} style={calNavBtn}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2, marginBottom: 4 }}>
        {DOW.map((d, i) => <div key={i} style={{ textAlign: 'center', fontSize: 10, color: '#c0c0c0', fontWeight: 700, padding: '2px 0' }}>{d}</div>)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
        {Array.from({ length: firstDow }).map((_, i) => <div key={`b${i}`} />)}
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
          const ymd = toYMD(new Date(viewYear, viewMonth, day));
          const isF = from && ymd === toYMD(from);
          const isT = to   && ymd === toYMD(to);
          const inR = inRange(day);
          return (
            <div key={day} onClick={() => pick(day)}
              onMouseEnter={() => picking === 'to' && setHovered(new Date(viewYear, viewMonth, day))}
              onMouseLeave={() => setHovered(null)}
              style={{ textAlign: 'center', padding: '5px 2px', fontSize: 12, borderRadius: 5, cursor: 'pointer',
                fontWeight: isF || isT ? 700 : 400,
                background: isF || isT ? '#111' : inR ? '#f0f0f0' : 'transparent',
                color: isF || isT ? '#fff' : '#333' }}>
              {day}
            </div>
          );
        })}
      </div>
      <p style={{ margin: '10px 0 0', fontSize: 11, color: '#bbb', textAlign: 'center' }}>
        {picking === 'from' ? 'Select start date' : 'Select end date'}
      </p>
    </div>
  );
}

const calNavBtn = { background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#999', padding: '0 4px', lineHeight: 1 };

// ── "Mark as DMed" button ───────────────────────────────────────────────────
function DmedButton({ dmed, onToggle }) {
  const [busy, setBusy] = useState(false);
  async function handle(e) {
    e.preventDefault(); e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try { await onToggle(); } catch (_) {} finally { setBusy(false); }
  }
  return (
    <button onClick={handle} disabled={busy}
      title={dmed ? 'Already DMed — click to unmark' : 'Mark this person as DMed'}
      style={{
        padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
        cursor: busy ? 'default' : 'pointer', whiteSpace: 'nowrap', transition: 'all 0.1s',
        border: dmed ? '1.5px solid #10b981' : '1.5px solid #d0d0d0',
        background: dmed ? '#10b981' : '#fff',
        color: dmed ? '#fff' : '#555',
        opacity: busy ? 0.6 : 1,
      }}
      onMouseEnter={e => { if (!busy && !dmed) e.currentTarget.style.borderColor = '#10b981'; }}
      onMouseLeave={e => { if (!dmed) e.currentTarget.style.borderColor = '#d0d0d0'; }}
    >
      {busy ? '…' : dmed ? '✓ DMed' : '✓ Mark DMed'}
    </button>
  );
}

// ── Intent Badge ──────────────────────────────────────────────────────────────

function IntentBadge({ intent }) {
  const [hover, setHover] = useState(false);
  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <span
        onMouseEnter={() => intent.reason && setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
          color: intent.color, background: intent.bg,
          border: `1px solid ${intent.border}`,
          whiteSpace: 'nowrap', cursor: intent.reason ? 'help' : 'default',
          display: 'flex', alignItems: 'center', gap: 3,
        }}
      >
        {intent.label}
      </span>
      {hover && intent.reason && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 6px)', right: 0, zIndex: 300,
          background: '#1a1a1a', color: '#f0f0f0', fontSize: 11, padding: '7px 10px',
          borderRadius: 7, maxWidth: 240, lineHeight: 1.5, whiteSpace: 'normal',
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
        }}>
          {intent.reason}
        </div>
      )}
    </div>
  );
}

// ── Lead Card ───────────────────────────────────────────────────────────────

function LeadCard({ entry, intent, personDmed, onToggleDmed }) {
  const d = parseDate(entry.capturedAt);
  const dateStr = d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  const timeStr = d ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';

  const baseBg = personDmed ? '#f0fdf4' : 'transparent';
  const hoverBg = personDmed ? '#e6fbee' : '#fafafa';

  const postLink = entry.postUrl || entry.profileUrl || null;
  const hasPostLink = !!entry.postUrl;

  return (
    <div style={{
      display: 'flex', gap: 14, padding: '16px 20px',
      borderBottom: '1px solid #f3f3f3',
      borderLeft: personDmed ? '3px solid #10b981'
        : intent.tier === 'hot' ? '3px solid #fca5a5' : intent.tier === 'warm' ? '3px solid #fde68a' : '3px solid transparent',
      background: baseBg,
      transition: 'background 0.12s',
    }}
      onMouseEnter={e => e.currentTarget.style.background = hoverBg}
      onMouseLeave={e => e.currentTarget.style.background = baseBg}
    >
      <a href={entry.profileUrl} target="_blank" rel="noopener noreferrer" style={{ flexShrink: 0, marginTop: 2 }}>
        <Avatar src={entry.profilePhoto} name={entry.name} size={40} />
      </a>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5, gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <a href={entry.profileUrl} target="_blank" rel="noopener noreferrer"
              style={{ fontWeight: 600, fontSize: 14, color: '#0f0f0f', textDecoration: 'none', whiteSpace: 'nowrap' }}>
              {entry.name}
            </a>
            <IntentBadge intent={intent} />
            {personDmed && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
                color: '#065f46', background: '#d1fae5', border: '1px solid #6ee7b7', whiteSpace: 'nowrap' }}>
                ✓ DMed
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <span style={{ fontSize: 11, color: '#ccc' }} title="When we found this lead">found {timeStr} · {dateStr}</span>
            {hasPostLink && (
              <a href={postLink} target="_blank" rel="noopener noreferrer"
                title="Open this post on Facebook"
                style={{ fontSize: 11, fontWeight: 600, color: '#6366f1', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                ↗ Post
              </a>
            )}
            <DmedButton dmed={personDmed} onToggle={() => onToggleDmed(entry)} />
          </div>
        </div>
        {postLink ? (
          <a href={postLink} target="_blank" rel="noopener noreferrer"
            title={hasPostLink ? 'Open this post on Facebook' : 'Open this profile on Facebook'}
            style={{ display: 'block', margin: 0, fontSize: 13.5, color: '#3a3a3a', lineHeight: 1.6, textDecoration: 'none' }}
            onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; e.currentTarget.style.color = '#1f1f1f'; }}
            onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; e.currentTarget.style.color = '#3a3a3a'; }}>
            {entry.postText}
          </a>
        ) : (
          <p style={{ margin: 0, fontSize: 13.5, color: '#3a3a3a', lineHeight: 1.6 }}>{entry.postText}</p>
        )}
      </div>
    </div>
  );
}

// ── Sidebar checkable item (search terms) ──────────────────────────────────────

function FilterItem({ name, count, checked, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 12px', cursor: 'pointer',
      borderRadius: 6, transition: 'background 0.1s', userSelect: 'none' }}
      onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <div onClick={onChange} style={{
        width: 16, height: 16, borderRadius: 4, flexShrink: 0,
        border: checked ? '2px solid #111' : '1.5px solid #d0d0d0',
        background: checked ? '#111' : '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.1s',
      }}>
        {checked && <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
          <path d="M1 3.5L3.5 6L8 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>}
      </div>
      <span style={{ fontSize: 13, color: '#333', flex: 1, lineHeight: 1.3 }}>{name}</span>
      <span style={{ fontSize: 11, color: '#bbb', fontVariantNumeric: 'tabular-nums' }}>{count}</span>
    </label>
  );
}

// ── Intent Filter Chips ───────────────────────────────────────────────────────

const INTENT_FILTERS = [
  { key: 'all',      label: 'All' },
  { key: 'hot',      label: '🔥 Hot' },
  { key: 'warm',     label: '✈️ Wants agent' },
  { key: 'provider', label: '📣 Agents' },
];

function IntentChips({ value, onChange, counts }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      {INTENT_FILTERS.map(f => {
        const active = value === f.key;
        const count = counts[f.key] ?? 0;
        return (
          <button key={f.key} onClick={() => onChange(f.key)} style={{
            padding: '5px 11px', borderRadius: 20, fontSize: 12, fontWeight: active ? 700 : 500,
            cursor: 'pointer', border: active ? '1.5px solid #111' : '1.5px solid #e5e5e5',
            background: active ? '#111' : '#fff',
            color: active ? '#fff' : '#555',
            whiteSpace: 'nowrap', transition: 'all 0.1s',
            display: 'flex', alignItems: 'center', gap: 5,
          }}>
            {f.label}
            <span style={{ fontSize: 10, opacity: 0.65, fontVariantNumeric: 'tabular-nums' }}>{count}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── Scrape Health panel ───────────────────────────────────────────────────────
// Mirrors the /fb-comments health panel: shows whether the travel scrape ran and how
// many leads it pulled, with full run history from travelAgentScrapeRuns.
const FB_HEALTH_STATUS = {
  success: { bg: '#dcfce7', fg: '#15803d', dot: '#16a34a', label: 'OK' },
  partial: { bg: '#fef3c7', fg: '#b45309', dot: '#d97706', label: 'Partial' },
  failed:  { bg: '#fee2e2', fg: '#b91c1c', dot: '#dc2626', label: 'Failed' },
  running: { bg: '#dbeafe', fg: '#1d4ed8', dot: '#2563eb', label: 'Running' },
};
function fmtRunTime(ts) {
  try {
    const d = ts && ts.toDate ? ts.toDate() : (ts ? new Date(ts) : null);
    return d ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
  } catch { return '—'; }
}
const fbRunMs = (r) => (r.startedAt && r.startedAt.toMillis ? r.startedAt.toMillis() : (r.startedAt ? new Date(r.startedAt).getTime() : 0));

function ScrapeHealthPanel({ runs }) {
  const [open, setOpen] = useState(false);
  const th = { padding: '6px 8px', fontWeight: 600, position: 'sticky', top: 0, background: '#fff', whiteSpace: 'nowrap' };
  const td = { padding: '6px 8px', verticalAlign: 'top', borderTop: '1px solid #efefef' };

  const sorted = [...runs].sort((a, b) => fbRunMs(b) - fbRunMs(a));
  const weekAgo = Date.now() - 7 * 864e5;
  const recent = sorted.filter(r => fbRunMs(r) >= weekAgo);
  const c = recent.reduce((a, r) => (a[r.status] = (a[r.status] || 0) + 1, a), {});
  const leads7 = recent.reduce((a, r) => a + (r.leadsScraped || 0), 0);
  const todayKey = new Date().toISOString().slice(0, 10);
  const todayRun = sorted.find(r => r.dateKey === todayKey);

  const todayPill = (() => {
    const s = todayRun ? (FB_HEALTH_STATUS[todayRun.status] || FB_HEALTH_STATUS.failed)
                       : { bg: '#f1f5f9', fg: '#94a3b8', dot: '#cbd5e1', label: 'No run yet' };
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 600, background: s.bg, color: s.fg }}>
        <span style={{ width: 7, height: 7, borderRadius: 999, background: s.dot }} />
        Today · {todayRun ? `${s.label}${todayRun.leadsScraped != null ? ` · ${todayRun.leadsScraped} leads` : ''}` : 'No run yet'}
      </span>
    );
  })();

  return (
    <div style={{ borderBottom: '1px solid #efefef', background: '#fff' }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 24px', cursor: 'pointer', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#888' }}>Scrape Health</span>
        {todayPill}
        <span style={{ fontSize: 12, color: '#aaa' }}>
          7d: <b style={{ color: '#15803d' }}>{c.success || 0}</b> ok · <b style={{ color: '#b45309' }}>{c.partial || 0}</b> partial · <b style={{ color: '#b91c1c' }}>{c.failed || 0}</b> failed · {leads7} leads
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#888' }}>{open ? '▾' : '▸'} {sorted.length} runs</span>
      </div>
      {open && (
        <div style={{ maxHeight: 300, overflowY: 'auto', padding: '0 24px 14px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: '#333' }}>
            <thead>
              <tr style={{ color: '#aaa', textAlign: 'left' }}>
                <th style={th}>Started</th>
                <th style={th}>Status</th>
                <th style={{ ...th, textAlign: 'right' }}>Scanned</th>
                <th style={{ ...th, textAlign: 'right' }}>Leads</th>
                <th style={th}>Source</th>
                <th style={th}>Note</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => {
                const s = FB_HEALTH_STATUS[r.status] || FB_HEALTH_STATUS.failed;
                return (
                  <tr key={r.id}>
                    <td style={td}>{fmtRunTime(r.startedAt)}</td>
                    <td style={td}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 999, background: s.bg, color: s.fg, fontWeight: 600, fontSize: 11 }}>
                        <span style={{ width: 6, height: 6, borderRadius: 999, background: s.dot }} />{s.label}
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: 'right', color: '#666' }}>{r.scanned != null ? r.scanned : '—'}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: r.leadsScraped > 0 ? '#111' : '#aaa' }}>{r.leadsScraped != null ? r.leadsScraped : '—'}</td>
                    <td style={{ ...td, color: '#888' }}>{r.source || '—'}</td>
                    <td style={{ ...td, color: '#aaa', maxWidth: 320 }}>{r.error || ''}</td>
                  </tr>
                );
              })}
              {sorted.length === 0 && (
                <tr><td colSpan={6} style={{ ...td, color: '#aaa', padding: '16px 8px' }}>No scrape runs recorded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function TravelAgentDashboard() {
  const [leads, setLeads]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selectedTerms, setSelectedTerms] = useState(new Set());
  const [search, setSearch]             = useState('');
  const [preset, setPreset]             = useState('all');
  const [customRange, setCustomRange]   = useState({ from: null, to: null });
  const [calOpen, setCalOpen]           = useState(false);
  const [intentFilter, setIntentFilter] = useState('all');
  const [sortMode, setSortMode]         = useState('intent'); // 'intent' | 'recent'
  const [scrapeRuns, setScrapeRuns]     = useState([]);
  const calRef = useRef(null);

  useEffect(() => {
    function down(e) { if (calRef.current && !calRef.current.contains(e.target)) setCalOpen(false); }
    document.addEventListener('mousedown', down);
    return () => document.removeEventListener('mousedown', down);
  }, []);

  // Travel-agent leads
  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'travelAgentLeads')), snap => {
      setLeads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, e => { console.error('travelAgentLeads subscription error:', e); setLoading(false); });
    return () => unsub();
  }, []);

  // Scrape run-health records
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'travelAgentScrapeRuns'), orderBy('startedAt', 'desc'), limit(120)),
      snap => setScrapeRuns(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      e => console.error('travelAgentScrapeRuns subscription error:', e)
    );
    return () => unsub();
  }, []);

  // Search-term breakdown for the sidebar (which query found each lead).
  const termCounts = useMemo(() => {
    const m = {};
    leads.forEach(l => { const q = l.searchQuery || 'other'; m[q] = (m[q] || 0) + 1; });
    return m;
  }, [leads]);
  const terms = useMemo(() => Object.keys(termCounts).sort(), [termCounts]);

  function toggleTerm(t) {
    setSelectedTerms(prev => {
      const next = new Set(prev);
      next.has(t) ? next.delete(t) : next.add(t);
      return next;
    });
  }
  function clearTerms() { setSelectedTerms(new Set()); }
  function selectAllTerms() { setSelectedTerms(new Set(terms)); }

  // Person-level DM tracking: a person counts as DMed if ANY of their leads is flagged.
  const dmedKeys = useMemo(() => {
    const s = new Set();
    leads.forEach(l => { if (l.dmed) s.add(personKey(l)); });
    return s;
  }, [leads]);

  async function toggleDmed(entry) {
    const key = personKey(entry);
    const isDmed = dmedKeys.has(key);
    const targets = isDmed
      ? leads.filter(l => l.dmed && personKey(l) === key)   // unmark every flagged lead
      : [entry];                                            // mark just the clicked one
    await Promise.all(targets.map(l =>
      updateDoc(doc(db, 'travelAgentLeads', l.id),
        isDmed ? { dmed: false, dmedAt: null } : { dmed: true, dmedAt: serverTimestamp() })
    ));
  }

  const dateRange = useMemo(() => {
    if (preset === 'custom') return customRange;
    return getPresetRange(preset);
  }, [preset, customRange]);

  // Attach intent to every lead.
  const withIntent = useMemo(() => leads.map(l => ({ ...l, intent: travelIntent(l) })), [leads]);

  const filtered = useMemo(() => {
    return withIntent
      .filter(l => {
        if (selectedTerms.size > 0 && !selectedTerms.has(l.searchQuery || 'other')) return false;
        const q = search.toLowerCase();
        if (q && !l.name?.toLowerCase().includes(q) && !l.postText?.toLowerCase().includes(q)) return false;
        if (dateRange?.from || dateRange?.to) {
          const d = parseDate(l.capturedAt);
          if (!d) return false;
          if (dateRange.from && d < dateRange.from) return false;
          if (dateRange.to   && d > dateRange.to)   return false;
        }
        if (intentFilter !== 'all' && l.intent.tier !== intentFilter) return false;
        return true;
      })
      .sort((a, b) => {
        if (sortMode === 'intent') {
          const diff = b.intent.score - a.intent.score;
          if (diff !== 0) return diff;
        }
        return (parseDate(b.capturedAt) || 0) - (parseDate(a.capturedAt) || 0);
      });
  }, [withIntent, selectedTerms, search, dateRange, intentFilter, sortMode]);

  // Count per tier for chips (before intent filter).
  const tierCounts = useMemo(() => {
    const base = withIntent.filter(l => {
      if (selectedTerms.size > 0 && !selectedTerms.has(l.searchQuery || 'other')) return false;
      const q = search.toLowerCase();
      if (q && !l.name?.toLowerCase().includes(q) && !l.postText?.toLowerCase().includes(q)) return false;
      if (dateRange?.from || dateRange?.to) {
        const d = parseDate(l.capturedAt);
        if (!d) return false;
        if (dateRange.from && d < dateRange.from) return false;
        if (dateRange.to   && d > dateRange.to)   return false;
      }
      return true;
    });
    const m = { all: base.length, hot: 0, warm: 0, provider: 0 };
    base.forEach(l => { if (m[l.intent.tier] !== undefined) m[l.intent.tier]++; });
    return m;
  }, [withIntent, selectedTerms, search, dateRange]);

  const customLabel = useMemo(() => {
    if (!customRange.from) return 'Custom';
    const f = customRange.from.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (!customRange.to) return `${f} →`;
    const t = customRange.to.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${f} – ${t}`;
  }, [customRange]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#fff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

      {/* Scrape health — full width */}
      <ScrapeHealthPanel runs={scrapeRuns} />

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>

        {/* ── Sidebar ── */}
        <div style={{ width: 260, flexShrink: 0, borderRight: '1px solid #efefef',
          background: '#fafafa', display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>

          {/* Brand */}
          <div style={{ padding: '24px 20px 16px', borderBottom: '1px solid #efefef' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111', letterSpacing: '-0.3px' }}>✈️ Travel Leads</div>
            <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>
              {loading ? '—' : `${leads.length} total · ${tierCounts.hot} hot · ${tierCounts.warm} wants agent`}
            </div>
          </div>

          {/* Date filters */}
          <div style={{ padding: '16px 12px 8px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#bbb', letterSpacing: '0.08em',
              textTransform: 'uppercase', padding: '0 4px', marginBottom: 6 }}>Time Range</div>
            {PRESETS.map(p => {
              const active = preset === p.key;
              return (
                <div key={p.key} ref={p.key === 'custom' ? calRef : null} style={{ position: 'relative' }}>
                  <button
                    onClick={() => {
                      setPreset(p.key);
                      if (p.key === 'custom') setCalOpen(o => !o);
                      else setCalOpen(false);
                    }}
                    style={{
                      width: '100%', textAlign: 'left', padding: '7px 12px', borderRadius: 6,
                      border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 400,
                      background: active ? '#111' : 'transparent',
                      color: active ? '#fff' : '#555',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}
                  >
                    <span>{p.key === 'custom' ? customLabel : p.label}</span>
                    {p.key === 'custom' && <span style={{ fontSize: 10, opacity: 0.6 }}>▾</span>}
                  </button>
                  {p.key === 'custom' && calOpen && (
                    <div style={{ position: 'absolute', left: 'calc(100% + 8px)', top: 0, zIndex: 200,
                      background: '#fff', border: '1px solid #e8e8e8', borderRadius: 10, padding: 16,
                      boxShadow: '0 12px 40px rgba(0,0,0,0.12)', width: 236 }}>
                      <Calendar from={customRange.from} to={customRange.to}
                        onChange={r => { setCustomRange(r); if (r.from && r.to) setCalOpen(false); }} />
                      {(customRange.from || customRange.to) && (
                        <button onClick={() => { setCustomRange({ from: null, to: null }); setPreset('all'); setCalOpen(false); }}
                          style={{ marginTop: 10, width: '100%', padding: '6px 0', fontSize: 12, color: '#888',
                            background: 'none', border: '1px solid #e5e5e5', borderRadius: 6, cursor: 'pointer' }}>
                          Clear dates
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Search-term filters */}
          <div style={{ padding: '12px 12px 0', flex: 1, overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0 4px', marginBottom: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#bbb', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                Search Terms
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={selectAllTerms} style={sidebarTextBtn}>All</button>
                <button onClick={clearTerms} style={sidebarTextBtn}>None</button>
              </div>
            </div>
            {loading ? (
              <div style={{ padding: '12px', fontSize: 12, color: '#ccc' }}>Loading…</div>
            ) : terms.map(t => (
              <FilterItem key={t} name={`“${t}”`} count={termCounts[t] || 0}
                checked={selectedTerms.has(t)} onChange={() => toggleTerm(t)} />
            ))}
          </div>

          {selectedTerms.size > 0 && (
            <div style={{ padding: '12px 16px', borderTop: '1px solid #efefef' }}>
              <span style={{ fontSize: 12, color: '#999' }}>{selectedTerms.size} term{selectedTerms.size > 1 ? 's' : ''} selected</span>
            </div>
          )}
        </div>

        {/* ── Main content ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

          {/* Topbar */}
          <div style={{ padding: '14px 24px', borderBottom: '1px solid #f3f3f3', display: 'flex',
            flexDirection: 'column', gap: 10 }}>

            {/* Row 1: search + sort + count */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                placeholder="Search posts or people…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ flex: 1, maxWidth: 360, padding: '7px 13px', border: '1px solid #e8e8e8',
                  borderRadius: 8, fontSize: 13, color: '#111', background: '#fff', outline: 'none' }}
              />

              {/* Sort toggle */}
              <div style={{ display: 'flex', borderRadius: 7, border: '1px solid #e5e5e5', overflow: 'hidden', flexShrink: 0 }}>
                {[['intent', 'By Intent'], ['recent', 'Recent']].map(([key, label]) => (
                  <button key={key} onClick={() => setSortMode(key)} style={{
                    padding: '6px 12px', fontSize: 12, fontWeight: sortMode === key ? 700 : 400,
                    background: sortMode === key ? '#111' : '#fff',
                    color: sortMode === key ? '#fff' : '#777',
                    border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>
                    {label}
                  </button>
                ))}
              </div>

              <span style={{ fontSize: 13, color: '#bbb', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {loading ? '—' : `${filtered.length} result${filtered.length !== 1 ? 's' : ''}`}
              </span>
            </div>

            {/* Row 2: intent chips */}
            <IntentChips value={intentFilter} onChange={setIntentFilter} counts={tierCounts} />
          </div>

          {/* Feed */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: '80px 0', textAlign: 'center', color: '#ddd', fontSize: 14 }}>Loading…</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: '80px 0', textAlign: 'center', color: '#ddd', fontSize: 14 }}>
                {leads.length === 0
                  ? 'No travel-agent leads yet — run the travel scraper to populate this feed.'
                  : 'No leads match your filters.'}
              </div>
            ) : filtered.map(entry => (
              <LeadCard key={entry.id} entry={entry}
                intent={entry.intent}
                personDmed={dmedKeys.has(personKey(entry))} onToggleDmed={toggleDmed} />
            ))}
          </div>
        </div>

      </div>{/* close inner flex (sidebar + main) */}
    </div>
  );
}

const sidebarTextBtn = {
  background: 'none', border: 'none', cursor: 'pointer',
  fontSize: 11, color: '#bbb', padding: '1px 4px', borderRadius: 4,
};
